import { useState, useEffect, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong, faDownload, faVideo } from '@fortawesome/free-solid-svg-icons';
import { useAiImageTypesGeneration } from '../../hooks/useAiImageTypesGeneration';
import { downloadOriginal } from '../../utils/exportMedia';
import { AuthedVideoPlayer } from '../../components/ui/AuthedVideoPlayer';
import { ApiPayment } from '../../apis/ApiPayment';
import { VideoernerationProps } from '../../utils/aiModels/video';
import { PromptArea } from '../../components/app/generation/PromptArea';
import { usePromptHook } from '../../hooks/usePromptHook';
import { parsePromptResponse } from '../../utils/chat/chatPropsParsing';
import { useIsMobile } from '../../hooks/useIsMobile';
import { StatePreview } from '../../components/app/generation/StatePreview';
import { aiStateTypes } from '../../types/ai/states';
import { useNavigateBack } from '../../hooks/useNavigationBack';

export const VideoGeneration = () => {
    const { t } = useTranslation(['generation','toolsApps','apps']);
    const isMobile = useIsMobile();
    const navigateBack = useNavigateBack();
    const [generatedVideos,setGeneratedImages] = useState<VideoernerationProps[]>([]);
    const [lastGeneration,setLastGeneration] = useState<{src:string, id:number}|null>();
    const [inputs, setInputs] = useState<{'promptPositive':string,'promptNegative':string,'seed':number}>({
        promptNegative:'',
        promptPositive: '',
        seed: 42
    });
    const [generationLLM,stateLLM,setStateLLM,handleRandomPromptExternal,handleCheckPromptExternal, _,insufficientCreditsDialogLLM] = usePromptHook();
    const [aiGenerationIsRunning, setAiGenerationIsRunning] = useState(false);
    const [generation,state,setState,getJobID,insufficientCreditsDialog] = useAiImageTypesGeneration();
    const [aiState, setAiState] = useState<aiStateTypes>(null);
    const [creationCosts, setCreationCosts] = useState<number|null>(0);

    // video_gen is a fixed price (no width/height/duration params yet), so estimate once on mount
    // instead of on every input change - see ApiPayment.GetCostsForVideoCreate.
    const updateCosts = async() => {
        try {
            const response = await ApiPayment.GetCostsForVideoCreate();
            setCreationCosts(response.costs);
        } catch {
            console.log('Error in calculation creation costs.');
        }
    };

    const handleGenerationResult = () => {
        setAiGenerationIsRunning(false);
        let videos = generation;
        //add image to history list
        const newGeneratedVideos:VideoernerationProps[] = [...generatedVideos];
        let lastID = newGeneratedVideos.length > 0 ? newGeneratedVideos[newGeneratedVideos.length-1].id : 0;
        newGeneratedVideos.push({
            'id':lastID+1,
            'promptPositive': inputs.promptPositive,
            'promptNegative': inputs.promptNegative,
            'src': videos
        });
        setLastGeneration({
            src: videos[0],
            id: lastID +1
        });
        setGeneratedImages(newGeneratedVideos);
    };

    const generateVideo = async() => {
        setState(null);
        setStateLLM(null);
        setAiGenerationIsRunning(true);
        //request to get job id
        const body = { positive_prompt: inputs.promptPositive, negative_prompt: inputs.promptNegative};
        setState('send');
        let path = 'generate/video'
        try {
            getJobID('video_gen',path,body);
        } catch(err){
            setState('error');
            setAiGenerationIsRunning(false);
        };
    };
    const handleInputChange = (event:ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        const name = event.target.name;
        setInputs(prev => ({...prev,[name]: value}));
    };
    const handleRandomPrompt = async () => {
        setState(null);
        setAiGenerationIsRunning(true);
        await handleRandomPromptExternal('create',true,'video'); 
    };
    const handleCheckPrompt = async () => {
        setState(null);
        setAiGenerationIsRunning(true);
        await handleCheckPromptExternal(inputs.promptPositive,inputs.promptNegative, 'create','video');
    };
    const downloadVideo = async (videoID:number) => {
        const videoObjekt: VideoernerationProps | undefined = generatedVideos.find(item => item.id === videoID);
        if(videoObjekt === undefined){return;}
        for(let videoUrl of videoObjekt.src){
              try {
                const fileName = videoUrl.replaceAll('\\','/').split('/').slice(-1)[0];
                await downloadOriginal(videoUrl, fileName);
            } catch (err) {
                console.error('Download fehlgeschlagen:', err);
            }
        };
    };

    useEffect(()=>{
        if(generation.length > 0){
            handleGenerationResult();
        }
    },[generation]);
    useEffect(()=>{
        updateCosts();
        document.title = t('apps:registry.app.videoGeneration.name')
    },[]);
    useEffect(()=>{
        if(state === 'done' || state === 'error' || state === 'unavailable' || stateLLM === 'error' || stateLLM === 'unavailable' || stateLLM === 'done'){
            setAiGenerationIsRunning(false);
        }
        if(state !== null){
            setStateLLM(null);
            setAiState(state);
        }
        if(stateLLM !== null){
            setState(null);
            setAiState(stateLLM);
        }
        if(!state && !stateLLM){
            setAiState(null);
        }
    },[state,stateLLM]);
    useEffect(() => {
        setAiGenerationIsRunning(false);
        setStateLLM(null);
        if (!generationLLM) return;
        const promptResponse = parsePromptResponse(generationLLM);
        if (!promptResponse) return;


        setInputs(prev => ({
            ...prev,
            prompt_positive: promptResponse.positive_prompt,
            prompt_negative: promptResponse.negative_prompt ?? ""
        }));
    }, [generationLLM]);

  return (
    <>
        {insufficientCreditsDialog}
        {insufficientCreditsDialogLLM}
        <div className="container-fluid h-100 pb-2">
            <div className='d-flex flex-column h-100'>
                <div className='row mx-0'>
                    <div className='col-auto p-2 pe-0'>
                        <button
                            onClick={() => navigateBack()}
                            className="btn btn-outline-secondary btn-sm ms-3"
                            title={t('toolsApps:back')}
                        >
                        <FontAwesomeIcon
                                icon={faArrowLeftLong}
                                size="1x"
                                title={t('back')}
                            />
                        </button>
                    </div>
                    <div className='col'/>
                    <div className='col-auto h3 align-items-center'>
                        {t('generateVideo')}
                    </div>
                    <div className='col'/>
                </div>
                <StatePreview state={aiState} />
                <div className={`row flex-grow-1 overflow-hidden p-2 ${!isMobile && generatedVideos.length === 0 ? "justify-content-center" : ""}`}>
                    <div className={`${!isMobile && generatedVideos.length > 0 ? "col-6" : isMobile ? "col-12" : "col-8"} h-100 p-0`}>
                        <div className="position-relative h-100 w-100 d-flex align-items-center justify-content-center overflow-hidden">
                            { lastGeneration
                                ?   <>
                                        <div className='text-center my-1 border border-secondary rounded d-block' style={{height:'520px', width:'auto', maxWidth:'100%'}}>
                                            <AuthedVideoPlayer
                                                url={lastGeneration.src}
                                                controls={ true}
                                                style={{
                                                    maxWidth: "100%",
                                                    maxHeight: isMobile ? "350px" : "100%",
                                                    width: "auto",
                                                    height: "auto",
                                                    objectFit: "contain"
                                                }}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary mt-0 position-absolute top-0 end-0 m-2 border border-secondary rounded"
                                            title={t("download.image")}
                                            onClick={() => downloadVideo(lastGeneration.id)}
                                            style={{ zIndex: 2 }}
                                        >
                                            <FontAwesomeIcon
                                                icon={faDownload}
                                                size="xl"
                                                className="bg-transparent me-2"
                                            />
                                        </button>
                                    </>
                                :   <div className={`text-center border border-secondary border-opacity-25 rounded mt-2 d-flex flex-column align-items-center justify-content-center ${isMobile ? 'h4' : 'h3'}`}
                                        style={{
                                            width: isMobile ? "180px" : "100%",
                                            height: isMobile ? "180px" : "100%",
                                            minHeight: isMobile ? "180px" : "250px"
                                        }}
                                    >
                                        <p className="m-0 p-0 opacity-25">
                                            <FontAwesomeIcon
                                                icon={faVideo}
                                                size={isMobile ? '1x' : "2xl"}
                                                className="me-2"
                                            />
                                            {t('noGeneratedVideo')}
                                        </p>
                                    </div>

                            }
                        </div>
                    </div>
                    {! isMobile &&(
                        <div className='col-6 h-100 px-2'>
                            {generatedVideos.slice().sort((a, b) => b.id - a.id).map(item=>(
                                <div className='row p-2 my-1 text-center align-items-center border-bottom border-secondary'>
                                    <div className='col-5 d-flex flex-wrap py-auto' style={{gap:'10px'}}>
                                        {item.src.map(video => (
                                            <div className='text-center my-1' style={{height:'350px', aspectRatio: '10 / 4', paddingLeft:'10%', paddingRight:'10%'}}>
                                                <AuthedVideoPlayer
                                                    url={video}
                                                    controls={ true}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    <div className='col d-flex flex-column justify-content-center text-start'>
                                        <div className='row mb-2'>
                                            <p className='fw-bold'>{t('prompt.positiveShort')}</p>
                                            <p className='mb-2'>{item.promptPositive}</p>
                                            <p className='fw-bold'>{t('prompt.negativeShort')}</p>
                                            <p className='mb-2'>{item.promptNegative}</p>
                                        </div>
                                        <div className='row mb-2'>
                                            <div className='col-auto p-0'>
                                                <button
                                                    className="btn btn-outline-secondary"
                                                    title={t('download.video')}
                                                    onClick={() => downloadVideo(item.id)}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faDownload}
                                                        size='xl'
                                                        className='bg-transparent me-2'
                                                    />
                                                    {t('download.video')}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            ))}
                        </div>
                    )}
                </div>
                <PromptArea
                    functionsDisabled={aiGenerationIsRunning}
                    credits={creationCosts ?? 0}
                    onCheckPrompt={handleCheckPrompt}
                    onRandomPrompt={handleRandomPrompt}
                    onGenerateImage={generateVideo}
                    promptNegative={inputs.promptNegative ?? ''}
                    promptPositive={inputs.promptPositive ?? ''}
                    onInputChange={handleInputChange}
                />
            </div>
        </div>
    </>
  );
};

export default VideoGeneration;